/**
 * Moving and turning modules on a roof plane (pure, tested in gizmo.test.ts): the maths behind the ring gizmo
 * (2D and 3D) and the "magnetic" placement.
 *
 * The gizmo sits on the selection's face, in its uv frame (u along the eave, v up the slope, both in the plane):
 * - the centre disc moves freely in the plane, the four arrows along ±u or ±v only;
 * - the two curved arrows turn the selection about its centre, around the face's normal (15° steps unless free);
 * - dragging over another face re-hosts the selection there: centres go through plan coordinates, the rotation is
 *   kept relative to the new face's eave.
 *
 * Two placement modes:
 * - free: the gizmo's moves and turns as they are (only the angle steps snap), modules may sit at any angle;
 * - magnetic: modules follow the faces drawn from the roof's lines: every move snaps to the face's module grid
 *   (rows along its eave, `snap.ts`), the rotation stays the face's (0), and a module never lands where it does
 *   not fit (setbacks, obstacles, other modules).
 */
import { planToUv, uvToXyz, type Vec2, type Vec3 } from "../editor/geo.ts";
import { faceContext, panelGeometry } from "../editor/layout.ts";
import { newKey, type ObstacleItem, type PanelItem } from "../editor/model.ts";
import { faceAt, type LiveFace, type LiveRoof } from "../editor/roof.ts";
import { bestFill, footprint, gridFor, stickyPlace, type FaceContext, type ModuleSize, type Orientation } from "../editor/snap.ts";
import type { S } from "../api/client";

export type PlaceMode = "free" | "magnetic";
export type Handle = "centre" | "+u" | "-u" | "+v" | "-v" | "rotate";
export const ANGLE_STEP_DEG = 15;
const RAD = Math.PI / 180;

export interface GizmoFrame {
  face: LiveFace;
  /** Selection centre in the face's uv, in plan and in 3D. */
  centreUv: Vec2;
  centrePlan: Vec2;
  centreXyz: Vec3;
  /** Ring radius: the selection's half-diagonal plus a margin, metres on the roof. */
  radius: number;
}

const toUv = (face: LiveFace, p: Vec2) => planToUv(face.frame, face.normal, face.d, p);

/** Where the gizmo of the selected modules sits (on the face of the first one). */
export function gizmoFrame(panels: PanelItem[], keys: Set<string>, roof: LiveRoof, module: ModuleSize, margin = 0.3): GizmoFrame | null {
  const chosen = panels.filter((p) => keys.has(p.key));
  if (!chosen.length) return null;
  const face = roof.faces.find((f) => f.id === chosen[0]!.face_id);
  if (!face) return null;
  const corners = chosen.flatMap((p) => panelGeometry(p, roof, module).plan);
  if (!corners.length) return null;
  const uvs = corners.map((c) => toUv(face, c));
  const centreUv: Vec2 = [(Math.min(...uvs.map((p) => p[0])) + Math.max(...uvs.map((p) => p[0]))) / 2, (Math.min(...uvs.map((p) => p[1])) + Math.max(...uvs.map((p) => p[1]))) / 2];
  const radius = Math.max(...uvs.map((p) => Math.hypot(p[0] - centreUv[0], p[1] - centreUv[1]))) + margin;
  const centreXyz = uvToXyz(face.frame, centreUv);
  return { face, centreUv, centrePlan: [centreXyz[0], centreXyz[1]], centreXyz, radius };
}

/** The drag's displacement in uv for a handle: an arrow keeps its own axis, the centre both. */
export function constrain(handle: Handle, delta: Vec2): Vec2 {
  if (handle === "+u" || handle === "-u") return [delta[0], 0];
  if (handle === "+v" || handle === "-v") return [0, delta[1]];
  return delta;
}

/** The angle (degrees, counter-clockwise in uv) the rotate handle has turned from `start` to `now` about `centre`. */
export function turnAngle(centre: Vec2, start: Vec2, now: Vec2): number {
  const a0 = Math.atan2(start[1] - centre[1], start[0] - centre[0]);
  const a1 = Math.atan2(now[1] - centre[1], now[0] - centre[0]);
  let d = (a1 - a0) / RAD;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}

export function snapAngle(deg: number, step = ANGLE_STEP_DEG, free = false): number {
  return free ? deg : Math.round(deg / step) * step;
}

/** The selection moved by `deltaUv` on `face` (modules of other faces move by the same plan offset). */
export function movePanels(panels: PanelItem[], keys: Set<string>, roof: LiveRoof, face: LiveFace, deltaUv: Vec2): PanelItem[] {
  const origin = uvToXyz(face.frame, [0, 0]);
  const moved = uvToXyz(face.frame, deltaUv);
  const planDelta: Vec2 = [moved[0] - origin[0], moved[1] - origin[1]];
  return panels.map((p) => {
    if (!keys.has(p.key)) return p;
    if (p.face_id === face.id) return { ...p, center: [p.center[0] + deltaUv[0], p.center[1] + deltaUv[1]] };
    const own = roof.faces.find((f) => f.id === p.face_id);
    if (!own) return p;
    const c = uvToXyz(own.frame, p.center);
    return { ...p, center: toUv(own, [c[0] + planDelta[0], c[1] + planDelta[1]]) };
  });
}

/** The selection turned by `deg` about `centreUv` (on `face`): centres swing round it, each module turns too. */
export function rotatePanels(panels: PanelItem[], keys: Set<string>, face: LiveFace, centreUv: Vec2, deg: number): PanelItem[] {
  const c = Math.cos(deg * RAD);
  const s = Math.sin(deg * RAD);
  return panels.map((p) => {
    if (!keys.has(p.key) || p.face_id !== face.id) return p;
    const dx = p.center[0] - centreUv[0];
    const dy = p.center[1] - centreUv[1];
    return { ...p, center: [centreUv[0] + c * dx - s * dy, centreUv[1] + s * dx + c * dy], rotation: normaliseDeg(p.rotation + deg) };
  });
}

export const normaliseDeg = (d: number) => {
  let r = ((d % 360) + 360) % 360;
  if (r > 180) r -= 360;
  return Math.abs(r) < 1e-9 ? 0 : r;
};

/** The selection moved onto `to`: centres through plan coordinates, rotation kept relative to the eave. */
export function rehost(panels: PanelItem[], keys: Set<string>, roof: LiveRoof, to: LiveFace): PanelItem[] {
  return panels.map((p) => {
    if (!keys.has(p.key) || p.face_id === to.id) return p;
    const from = roof.faces.find((f) => f.id === p.face_id);
    if (!from) return p;
    const c = uvToXyz(from.frame, p.center);
    return { ...p, face_id: to.id, center: toUv(to, [c[0], c[1]]) };
  });
}

/** The face under a plan point. */
export function faceUnder(roof: LiveRoof, p: Vec2): LiveFace | null {
  return faceAt(roof, p);
}

export interface PlaceContext {
  roof: LiveRoof;
  panels: PanelItem[];
  obstacles: ObstacleItem[];
  setbacks: S["SetbacksOutput"] | null | undefined;
  module: ModuleSize;
  gap: number;
}

/**
 * Magnetic: the selection snapped onto its face's module grid after a move (each module to the nearest grid cell
 * where it fits, rotation back to the face's 0). A module that fits nowhere near stays where it was dragged (and
 * shows its conflict).
 */
export function magnetise(ctx: PlaceContext, keys: Set<string>): PanelItem[] {
  // phase 6: a face's usable area, obstacles and grid are made once, and each module is checked only against the
  // modules near it (a 500-module drop took over a second when every module rebuilt its face with all the others)
  const panels = ctx.panels.slice();
  const index = new Map(panels.map((p, i) => [p.key, i]));
  const onFace = new Map<string, number[]>();
  panels.forEach((p, i) => onFace.set(p.face_id, [...(onFace.get(p.face_id) ?? []), i]));
  const faces = new Map<string, { fc: FaceContext; grids: Map<string, ReturnType<typeof gridFor>> }>();
  const radius = 2;
  const pitch = Math.max(ctx.module.width_m, ctx.module.height_m) + ctx.gap;
  const reach = (radius + 1) * 1.5 * pitch + 2 * Math.hypot(ctx.module.width_m, ctx.module.height_m);
  for (const p0 of ctx.panels) {
    if (!keys.has(p0.key)) continue;
    const i = index.get(p0.key)!;
    const p = panels[i]!;
    const face = ctx.roof.faces.find((f) => f.id === p.face_id);
    if (!face) continue;
    let base = faces.get(face.id);
    if (!base) faces.set(face.id, (base = { fc: faceContext(face, ctx.roof, [], ctx.obstacles, ctx.setbacks, ctx.module), grids: new Map() }));
    let grid = base.grids.get(p.orientation);
    if (!grid) {
      // the grid of the modules that stay (the selection snaps onto it)
      const placed = (onFace.get(face.id) ?? []).map((j) => panels[j]!).filter((q) => !keys.has(q.key)).map((q) => ({ center: q.center, orientation: q.orientation, rotationDeg: q.rotation }));
      grid = { ...gridFor(placed, base.fc.areas, p.orientation, ctx.module, ctx.gap), rotationDeg: 0 };
      base.grids.set(p.orientation, grid);
    }
    const others = (onFace.get(face.id) ?? [])
      .filter((j) => j !== i && Math.hypot(panels[j]!.center[0] - p.center[0], panels[j]!.center[1] - p.center[1]) <= reach)
      .map((j) => footprint(panels[j]!.center, panels[j]!.orientation, panels[j]!.rotation, ctx.module));
    const spot = stickyPlace(p.center, { ...base.fc, others }, grid, p.orientation, ctx.module, radius);
    if (spot) panels[i] = { ...p, center: spot, rotation: 0 };
  }
  return panels;
}

/** A new module where the pointer is: magnetic snaps it to the face's grid (null when nothing fits near); free
 * puts it there, rows along the eave. */
export function placeAt(ctx: PlaceContext, plan: Vec2, orientation: Orientation, mode: PlaceMode): PanelItem | null {
  const face = faceAt(ctx.roof, plan);
  if (!face) return null;
  const uv = toUv(face, plan);
  if (mode === "free") return { key: newKey("p"), id: null, face_id: face.id, orientation, center: uv, rotation: 0 };
  const fc = faceContext(face, ctx.roof, ctx.panels, ctx.obstacles, ctx.setbacks, ctx.module);
  const placed = ctx.panels.filter((q) => q.face_id === face.id).map((q) => ({ center: q.center, orientation: q.orientation, rotationDeg: q.rotation }));
  const grid = { ...gridFor(placed, fc.areas, orientation, ctx.module, ctx.gap), rotationDeg: 0 };
  const spot = stickyPlace(uv, fc, grid, orientation, ctx.module, 3);
  return spot ? { key: newKey("p"), id: null, face_id: face.id, orientation, center: spot, rotation: 0 } : null;
}

/** Fills a face with as many modules as fit (the layout stage's packing: rows along the eave). */
export function fillFace(ctx: PlaceContext, face: LiveFace, preferred: Orientation): PanelItem[] {
  const fc = faceContext(face, ctx.roof, ctx.panels, ctx.obstacles, ctx.setbacks, ctx.module);
  const placed = ctx.panels.filter((q) => q.face_id === face.id).map((q) => ({ center: q.center, orientation: q.orientation, rotationDeg: q.rotation }));
  const fill = bestFill(fc, placed, preferred, ctx.module, ctx.gap);
  return fill.centers.map((center) => ({ key: newKey("p"), id: null, face_id: face.id, orientation: fill.orientation, center, rotation: fill.grid.rotationDeg }));
}
